import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type Ref,
  type ReactNode
} from 'react'
import { DIALOG_LAYER_CLOSE_HOLD_CLASS } from '#components/DialogPrimitives'
import { LAYER_Z_INDEX } from '#lib/layers'
import { cn } from '#lib/utils'

export { LAYER_Z_INDEX }

export type LayerTargetRole = 'modal' | 'blockingModal' | 'transient' | 'tooltip'

export interface LayerPortalTarget {
  container: HTMLElement | null
  zIndex: number
}

type LayerTargets = Record<LayerTargetRole, LayerPortalTarget>

const DEFAULT_LAYER_TARGETS: LayerTargets = {
  modal: { container: null, zIndex: LAYER_Z_INDEX.globalModal },
  blockingModal: { container: null, zIndex: LAYER_Z_INDEX.blockingModal },
  transient: { container: null, zIndex: LAYER_Z_INDEX.globalTransient },
  tooltip: { container: null, zIndex: LAYER_Z_INDEX.globalTooltip }
}

const LayerHostContext = createContext<LayerTargets>(DEFAULT_LAYER_TARGETS)

export function LayerHostProvider({
  targets,
  children
}: {
  targets: Partial<LayerTargets>
  children: ReactNode
}): React.JSX.Element {
  const inheritedTargets = useContext(LayerHostContext)
  const value = useMemo(() => ({ ...inheritedTargets, ...targets }), [inheritedTargets, targets])

  return <LayerHostContext.Provider value={value}>{children}</LayerHostContext.Provider>
}

export function useLayerPortalTarget(role: LayerTargetRole): LayerPortalTarget {
  return useContext(LayerHostContext)[role]
}

export interface DialogLayerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  children: ReactNode
  /** 承载 Radix 弹窗时传入弹窗的开关状态，关闭时弹窗层保留到退场动画播完。 */
  open?: boolean
  position?: 'fixed' | 'absolute'
  zIndex: number
}

export const DialogLayer = forwardRef<HTMLDivElement, DialogLayerProps>(function DialogLayer(
  { children, className, open, position = 'fixed', style, zIndex, ...props },
  forwardedRef
) {
  const [layerElement, setLayerElement] = useState<HTMLDivElement | null>(null)
  const setLayerRef = useCallback(
    (element: HTMLDivElement | null) => {
      setLayerElement(element)
      assignRef(forwardedRef, element)
    },
    [forwardedRef]
  )
  const targets = useMemo<Partial<LayerTargets>>(
    () => ({
      transient: {
        container: layerElement,
        zIndex: LAYER_Z_INDEX.dialogTransient
      },
      tooltip: {
        container: layerElement,
        zIndex: LAYER_Z_INDEX.dialogTooltip
      }
    }),
    [layerElement]
  )
  const layerStyle: CSSProperties = { ...style, zIndex }

  return (
    <LayerHostProvider targets={targets}>
      <div
        ref={setLayerRef}
        data-dialog-layer="true"
        data-state={open === undefined ? undefined : open ? 'open' : 'closed'}
        {...props}
        className={cn(
          position,
          'inset-0 isolate pointer-events-none',
          DIALOG_LAYER_CLOSE_HOLD_CLASS,
          className
        )}
        style={layerStyle}
      >
        {children}
      </div>
    </LayerHostProvider>
  )
})

/**
 * 弹窗层只在弹窗打开及其退场动画期间挂载（上面的 `DialogLayer`），所以文档里未在退场的弹窗层
 * 就是「当前有弹窗打开」的事实源。供挂在弹窗之外、需要区分「点到弹窗上」和「点到我外面」的浮层
 * 使用，避免逐个弹窗补开关。退场中的不算：随标签页一起隐藏的弹窗播不了退场动画，不能一直挡住
 * 快捷键和点击。
 */
export function isDialogLayerOpen(): boolean {
  if (typeof document === 'undefined') return false
  return Boolean(document.querySelector('[data-dialog-layer="true"]:not([data-state="closed"])'))
}

function assignRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (typeof ref === 'function') {
    ref(value)
  } else if (ref) {
    ref.current = value
  }
}
